import { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { GenericTable, UserActionsMenu } from '@components';
import type { UserActionsMenuOption } from '@components';
import { ClientTableRow, useClientsTable } from '../hooks/useClientsTable';
import { getClientsTableColumns } from '../utils/clientsTableColumns';
import AssignOwnerRoleModal from '../../../Dashboard/components/ManageUsers/components/Modals/AssignOwnerRoleModal';
import { CLIENT_USER_ROLE, CLIENT_USER_STATUS } from '@api/types/client-user-management';
import {
  InviteUserModal,
  RemoveAccessModal,
} from '../../../Dashboard/components/ManageUsers/components/Modals';
import { genericTexts } from '@utils/translations/en';
import useGetCompanyAccess from '@hooks/api/useGetCompanyAccess';

const TABLE_META = {
  chartType: 'MULTI_LEVEL_TABLE',
};

type ClientsTableProps = {
  pageNumber: number;
  pageSize: number;
  searchQuery: string;
  onPageReset: () => void;
  onTotalChange: (total: number) => void;
  onEditCompany?: (row: ClientTableRow) => void;
};

const ClientsTable = ({
  pageNumber,
  pageSize,
  searchQuery,
  onPageReset,
  onTotalChange,
  onEditCompany,
}: ClientsTableProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<ClientTableRow | null>(null);
  const [showAssignOwnerModal, setShowAssignOwnerModal] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showInviteUserModal, setShowInviteUserModal] = useState(false);

  const {
    companySubmodules,
    setCompanySubmodules,
    fetchCompanyAccess,
    isLoading: isLoadingDashboards,
  } = useGetCompanyAccess({
    clientCode: selectedUser?.clientCode ?? '',
    enabled: false,
  });

  const {
    rows,
    isLoading,
    total,
    sort,
    sortOrder,
    handleSort,
    roleFilter,
    handleRoleFilterChange,
    statusFilter,
    handleStatusFilterChange,
    refetch,
  } = useClientsTable({
    pageNumber,
    pageSize,
    searchQuery,
    onPageReset,
  });

  useEffect(() => {
    onTotalChange(total);
  }, [total, onTotalChange]);

  useEffect(() => {
    if (showInviteUserModal && selectedUser?.clientCode) {
      void fetchCompanyAccess();
    }
  }, [showInviteUserModal, selectedUser?.clientCode]);

  const handleInviteUser = (row: ClientTableRow) => {
    if (!row.clientCode) return;
    setSelectedUser(row);
    setShowInviteUserModal(true);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: ClientTableRow) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedUser(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const closeModal = () => {
    setShowDeleteConfirmation(false);
  };

  const handleOnSuccess = () => {
    refetch();
    closeModal();
  };

  const menuOptions: UserActionsMenuOption[] = selectedUser
    ? [
        ...(selectedUser.role !== CLIENT_USER_ROLE.OWNER
          ? [
              {
                label: genericTexts.makeOwner,
                onClick: () => {
                  handleMenuClose();
                  setShowAssignOwnerModal(true);
                },
              },
            ]
          : []),
        {
          label: genericTexts.cloneUser,
          onClick: () => {
            handleMenuClose();
            void handleInviteUser(selectedUser);
          },
        },
        {
          label: genericTexts.removeAccess,
          isDestructive: true,
          onClick: () => {
            handleMenuClose();
            setShowDeleteConfirmation(true);
          },
        },
      ]
    : [];

  const columns = useMemo(
    () =>
      getClientsTableColumns({
        onActionClick: handleMenuOpen,
        onEditCompany: (row) => onEditCompany?.(row),
        onInviteUser: handleInviteUser,
        sort,
        sortOrder,
        onSort: handleSort,
        roleFilter,
        onRoleFilterChange: handleRoleFilterChange,
        statusFilter,
        onStatusFilterChange: handleStatusFilterChange,
      }),
    [
      sort,
      sortOrder,
      handleSort,
      onEditCompany,
      roleFilter,
      handleRoleFilterChange,
      statusFilter,
      handleStatusFilterChange,
    ],
  );

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      {selectedUser?.membershipGrantId && (
        <UserActionsMenu
          anchorEl={anchorEl}
          onClose={handleMenuClose}
          options={menuOptions}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        />
      )}

      <GenericTable<ClientTableRow>
        data={rows}
        columns={columns}
        meta={TABLE_META}
        depth={1}
        hideExpandIfNoChildren
        isLoading={isLoading}
      />
      {showAssignOwnerModal && selectedUser?.membershipGrantId && selectedUser?.clientCode && (
        <AssignOwnerRoleModal
          open={showAssignOwnerModal}
          clientCode={selectedUser?.clientCode}
          selectedUserEmail={selectedUser?.user}
          selectedUserMembershipGrantId={selectedUser?.membershipGrantId}
          onClose={() => {
            setShowAssignOwnerModal(false);
            setSelectedUser(null);
          }}
          onSubmit={refetch}
        />
      )}
      {showDeleteConfirmation && selectedUser?.membershipGrantId && selectedUser?.clientCode && (
        <RemoveAccessModal
          open
          clientCode={selectedUser?.clientCode}
          selectedUserEmail={selectedUser?.user}
          selectedUserStatus={selectedUser?.status as CLIENT_USER_STATUS}
          selectedUserMembershipGrantId={selectedUser?.membershipGrantId}
          onClose={closeModal}
          onSubmit={handleOnSuccess}
        />
      )}
      {showInviteUserModal && selectedUser?.clientCode && (
        <InviteUserModal
          isLoadingDashboards={isLoadingDashboards}
          open={showInviteUserModal}
          clientCode={selectedUser?.clientCode}
          companyName={selectedUser?.company}
          dashboardsList={companySubmodules.map((submodule) => ({
            submoduleId: submodule.submoduleId,
            submoduleName: submodule.name,
            submoduleType: submodule.submoduleType ?? '',
          }))}
          initialAccess={
            selectedUser?.role
              ? {
                  companyRole: selectedUser?.role,
                  submoduleIds:
                    selectedUser?.dashboardAccess?.submodules?.map(
                      (submodule) => submodule.submoduleId,
                    ) ?? [],
                }
              : undefined
          }
          onClose={() => {
            setShowInviteUserModal(false);
            setCompanySubmodules([]);
          }}
          onSuccess={refetch}
        />
      )}
    </Box>
  );
};

export default ClientsTable;
